import FormData from 'form-data'
import fetch from 'node-fetch'

import { HttpError } from '../errors'

// Everything the CLI sends to a Flowrunner console. The counterpart of `server/`, which only listens.
//
// A non-2xx response becomes an HttpError carrying the status as a number, so callers can tell an expired
// token from a rejected package without reading the message.
//
// This is also the only module that still says `appId` / `appName`. The console calls a workspace an "app"
// in its URLs, its query strings and its token-exchange response; everything on our side of this boundary
// calls it a workspace — `workspaceId`, `workspaceName`, and those are the names `flowrunner.json` uses too.
// The translation happens here so that vocabulary does not leak into the rest of the CLI.

// ─── Authorization ───────────────────────────────────────────────────────────

/** What the console sends back. `appId` is its name for the workspace id. */
interface ExchangeResponse {
  token: string
  appId: string
  appName?: string
}

export interface ExchangeResult {
  token: string
  workspaceId: string
  /** Absent from an older console; the CLI falls back to showing the id alone. */
  workspaceName?: string
}

/**
 * Where to send the user to authorize the CLI and choose a workspace.
 *
 * Built here rather than at the call site because the query string is the console's contract — `appId`
 * included — and this module is where that vocabulary is allowed to live.
 */
export function authorizeUrl(
  { serverUrl, callbackUrl, workspaceId }: { serverUrl: string; callbackUrl: string; workspaceId?: string }
): string {
  const url = `${ serverUrl }/developer/cli/authorize` +
    `?callback=${ encodeURIComponent(callbackUrl) }` +
    '&clientType=cloud-code-deploy'

  // A workspace the project is already attached to is pre-selected on the page — shown as a disabled
  // control, with a "use another workspace" link beside it. Pre-selected, not locked: moving a project
  // between workspaces has to be possible without hand-editing flowrunner.json.
  return workspaceId ? `${ url }&appId=${ encodeURIComponent(workspaceId) }` : url
}

/**
 * Where a workspace's custom extensions live in the console.
 *
 * The path segment is the workspace *name*: `/app/:appName/…` is the console's own route shape, and this
 * module is where that vocabulary is allowed to live.
 *
 * Builds unconditionally. Whether there is a name worth linking to is the caller's question — a project with
 * none recorded has nothing to put in that slot, and an id there would not resolve.
 */
export function customExtensionsUrl(serverUrl: string, workspaceName: string): string {
  return `${ serverUrl }/app/${ encodeURIComponent(String(workspaceName)) }/custom-extensions`
}

export async function exchangeCodeForToken(serverUrl: string, code: string): Promise<ExchangeResult> {
  const url = `${ serverUrl }/developer/cli/token/exchange`

  const response = await fetch(url, {
    method : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body   : JSON.stringify({ code }),
  })

  if (!response.ok) {
    throw new HttpError(response.status, await response.text(), url, 'Token exchange')
  }

  const body = await response.json() as ExchangeResponse

  return { token: body.token, workspaceId: body.appId, workspaceName: body.appName }
}

export async function revokeToken(serverUrl: string, token: string): Promise<void> {
  try {
    await fetch(`${ serverUrl }/developer/cli/token/revoke`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ token }),
    })
  } catch {
    // Best effort: the local token is being deleted either way, and a revoke that never reached the server
    // is not a reason to leave the user still looking logged in.
  }
}

// ─── Deploy ──────────────────────────────────────────────────────────────────

export interface DeployCredentials {
  serverUrl: string
  workspaceId: string
  deployToken: string
}

/**
 * A file uploaded alongside the package — today the icons a service declares.
 *
 * Declared here rather than imported from `operations/deploy`: this layer knows how to send bytes with a
 * name attached, and nothing about what makes a file worth sending.
 */
export interface UploadFile {
  serviceId: string
  relativePath: string
  buffer: Buffer
}

export interface UploadResult {
  packageId: string
  serviceId: string
  serviceName: string
  sourceHash: string
}

/** One service in the workspace, as `flowrunner pull` sees it. */
export interface RemoteService {
  serviceId: string
  serviceName: string
  /** The version, and — because it covers the service folder and nothing else — comparable to a local copy. */
  sourceHash: string
}

/**
 * Upload one service: its zip, its definition, and any icons it declares.
 *
 * One service per request — the server activates exactly what it receives and leaves every other service in
 * the workspace alone, which is what lets several projects deploy into the same workspace.
 */
export async function uploadService(
  credentials: DeployCredentials,
  zipBuffer: Buffer,
  definition: object,
  publicFiles: UploadFile[] = []
): Promise<UploadResult> {
  const form = new FormData()

  form.append('file', zipBuffer, {
    filename   : 'service.zip',
    contentType: 'application/zip',
  })
  form.append('definition', JSON.stringify(definition))

  for (const file of publicFiles) {
    // Just the path: the request already carries exactly one service, so the name has nothing to qualify.
    form.append('publicFiles', file.buffer, {
      filename   : file.relativePath,
      contentType: 'application/octet-stream',
    })
  }

  const url = `${ credentials.serverUrl }/api/manage/app/${ credentials.workspaceId }/custom-extensions/deploy`

  const response = await fetch(url, {
    method : 'POST',
    headers: {
      'deploy-token': credentials.deployToken,
      ...form.getHeaders(),
    },
    body: form,
  })

  if (!response.ok) {
    throw new HttpError(response.status, await response.text(), url, 'Deploy')
  }

  return response.json() as Promise<UploadResult>
}

// ─── Pull ────────────────────────────────────────────────────────────────────

/** The workspace's active services — one entry per service, whichever project deployed it. */
export async function listRemoteServices(credentials: DeployCredentials): Promise<RemoteService[]> {
  const url = `${ credentials.serverUrl }/api/manage/app/${ credentials.workspaceId }/custom-extensions/cli/services`

  const response = await fetch(url, {
    headers: { 'deploy-token': credentials.deployToken },
  })

  if (!response.ok) {
    throw new HttpError(response.status, await response.text(), url, 'List services')
  }

  return response.json() as Promise<RemoteService[]>
}

/** The zip of a service's active version. */
export async function downloadRemoteService(credentials: DeployCredentials, serviceId: string): Promise<Buffer> {
  const url = `${ credentials.serverUrl }/api/manage/app/${ credentials.workspaceId }` +
    `/custom-extensions/cli/services/${ encodeURIComponent(serviceId) }/download`

  const response = await fetch(url, {
    headers: { 'deploy-token': credentials.deployToken },
  })

  if (!response.ok) {
    throw new HttpError(response.status, await response.text(), url, 'Download service')
  }

  return Buffer.from(await response.arrayBuffer())
}
